import { cartAtom, CartCompositionType } from 'src/state/cart/cartAtom';
import { useLocale } from '@repo/i18n';
import { Grid, Paper, useTheme } from '@mui/material';
import { CartItem } from '@repo/types';
import Summary, { SummaryFeatures } from 'src/components/domain/summary/Summary';
import {
    ContinueRebookingButton,
    ContinueShoppingButton,
    NextButton,
} from 'src/components/domain/summary/SummaryButtons';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';
import { findUrlQueryParam } from '@repo/common-utils/query-params-helper';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import { useCallback, useMemo, useState, type JSX } from 'react';
import { usePriceSummary } from 'src/hooks/domain/usePriceSummary';
import { getCartItemId } from '@repo/widget-utils/cart/cartUtils';
import { SummaryCartItem } from 'src/components/domain/summary/summary-mapping';
import useSummaryCartItemsByType from 'src/hooks/domain/useSummaryCartItemsByType';
import { clearCartEvent, removeFromCartEvent } from 'src/state/cart/cart.reducer';
import { useAtomReducer } from 'src/hooks/useAtomReducer';
import { voucherAtom } from 'src/state/voucher/voucherAtom';
import EditSummaryItemModal from 'src/components/domain/summary/EditSummaryItemModal';
import { octoCartAtom } from 'src/state/octo-cart/octoCartAtom';
import { cancelOctoBooking, cancelOctoOrder } from '@repo/widget-utils/services/fetchers/octo-api';
import { clearOctoCartEvent, setOctoCartDirtyEvent } from 'src/state/octo-cart/octoCart.reducer';
import useOctoOrderInCart from 'src/hooks/domain/cart/useOctoOrderInCart';

export default function BasketSummary(props: {
    goToCheckout: () => void;
    onCloseModal: () => void;
    cartComposition: CartCompositionType;
    cartItems: CartItem[];
    activeView: 'regular' | 'waitingList';
    isWaitingListMode: boolean;
    nextButtonText: string;
    headingText?: string;
    showShareButton: boolean;
}): JSX.Element {
    const { goToCheckout, onCloseModal, nextButtonText, headingText, showShareButton } = props;

    const { t } = useLocale();
    const theme = useTheme();
    const customizations = useCustomizations();

    const configurations = useConfigurations();

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [editCartItem, setEditCartItem] = useState<CartItem | null>(null);

    const rebookReturnUrl = findUrlQueryParam('rebookReturnUrl');

    const basketSummaryFeatures = useMemo(() => {
        const showPromoCodeSection = !!configurations.enablePromoCodes && !props.isWaitingListMode;

        const invertedColorsClass = {
            backgroundColor: customizations.bookingWidgetColor,
            color: customizations.bookingWidgetColorContrast,
            '& .MuiIconButton-root': {
                color: customizations.secondaryColorContrast,
            },
            '& .MuiSvgIcon-root': {
                color: customizations.secondaryColorContrast,
            },
        };

        return {
            showShare: showShareButton,
            showPromoCodeSection: showPromoCodeSection,
            promoCodeSectionSx: { mt: theme.spacing(1) }, // add margin-top to get some spacing between the separator line above this component.
            promoCodeWritable: false,
            giftcardWriteable: false,
            enableCartClearing: true,
            enableItemEditing: true,
            showGiftcardValue: false,
            invertedColorsClass: invertedColorsClass,
        } as SummaryFeatures;
    }, [
        configurations.enablePromoCodes,
        props.isWaitingListMode,
        showShareButton,
        theme,
        customizations,
    ]);

    const [{ appliedPromoCode }] = useAtomReducer(voucherAtom);

    const { octoOrder: octoOrderInCart, isLoading: isLoadingOctoOrderInCart } =
        useOctoOrderInCart();

    const summaryCartItemsByType = useSummaryCartItemsByType(
        props.cartItems,
        isLoadingOctoOrderInCart,
        octoOrderInCart,
        appliedPromoCode,
    );

    const priceSummary = usePriceSummary(
        props.cartItems,
        isLoadingOctoOrderInCart,
        octoOrderInCart,
        appliedPromoCode,
    );

    const removeCartItem = useCallback(
        async (summaryCartItem: SummaryCartItem) => {
            if (configurations.octo) {
                await cancelOctoBooking(summaryCartItem.uniqueKey, {
                    reason: 'User requested cancellation',
                });
                await octoCartAtom.update(setOctoCartDirtyEvent());
            } else {
                const cartItem = props.cartItems.find(
                    (ci) => getCartItemId(ci) === summaryCartItem.uniqueKey,
                )!;
                cartAtom.update(removeFromCartEvent(cartItem));
            }
        },
        [configurations.octo, props.cartItems],
    );

    const onEditClick = useCallback(
        (e: React.MouseEvent, summaryCartItem: SummaryCartItem) => {
            const cartItem = props.cartItems.find(
                (ci) => getCartItemId(ci) === summaryCartItem.uniqueKey,
            )!;

            if (e.currentTarget instanceof HTMLElement) {
                setAnchorEl(e.currentTarget);
                setEditCartItem(cartItem);
            }
        },
        [props.cartItems],
    );

    const onClearCart = basketSummaryFeatures.enableCartClearing
        ? async () => {
              if (configurations.octo && octoOrderInCart) {
                  await cancelOctoOrder(octoOrderInCart.id, {
                      reason: 'User requested cancellation',
                  });
                  await octoCartAtom.update(clearOctoCartEvent());
              } else {
                  await cartAtom.update(clearCartEvent());
              }
          }
        : undefined;

    return (
        <>
            <Grid
                component={Paper}
                mt={theme.spacing(2)}
                p={theme.spacing(3, 3, 2, 3)}
                maxWidth="400px"
                width="100%"
                sx={{
                    [theme.breakpoints.down('md')]: {
                        width: '100%',
                        maxWidth: '440px',
                        marginTop: theme.spacing(2),
                        marginBottom: theme.spacing(2),
                    },
                    ...basketSummaryFeatures.invertedColorsClass,
                }}
                variant="outlined"
            >
                <Summary
                    heading={headingText}
                    features={basketSummaryFeatures}
                    priceSummary={priceSummary}
                    summaryCartItemsByType={summaryCartItemsByType}
                    onRemoveCartItem={removeCartItem}
                    onEditClick={onEditClick}
                    onClearCart={onClearCart}
                />

                <Grid container justifyContent="space-between" mt={theme.spacing(4)}>
                    <ContinueShoppingButton
                        text={t.continue_shopping}
                        href={configurations.continueShoppingUrl}
                        onClick={configurations.continueShoppingUrl ? undefined : onCloseModal}
                    ></ContinueShoppingButton>

                    <NextButton text={nextButtonText} onClick={goToCheckout}></NextButton>
                </Grid>

                {rebookReturnUrl && (
                    <Grid display="flex" justifyContent="end">
                        <ContinueRebookingButton
                            text={t.continueWithRebooking}
                            href={rebookReturnUrl}
                        ></ContinueRebookingButton>
                    </Grid>
                )}

                {Boolean(anchorEl) && editCartItem && (
                    <EditSummaryItemModal
                        cartItem={editCartItem}
                        anchorEl={anchorEl}
                        onClose={() => {
                            setAnchorEl(null);
                            setEditCartItem(null);
                        }}
                    ></EditSummaryItemModal>
                )}
            </Grid>
        </>
    );
}
